import React, { useState, useCallback } from 'react';
import { Check, ChevronRight } from 'lucide-react';
import type { ValidationConfig, ValidationMode, MonitoringConfig } from '../../types/issue';
import { VALIDATION_MODE_LABELS } from '../../types/issue';

interface ProblemValidationWizardProps {
  validationConfig: ValidationConfig | null;
  monitoringConfig: MonitoringConfig | null;
  issueStatus: string;
  onSaveValidation: (config: ValidationConfig) => Promise<void>;
  onSaveMonitoring: (config: MonitoringConfig) => Promise<void>;
  onCompleteValidation: (result: 'passed' | 'failed') => Promise<void>;
}

const MODES: ValidationMode[] = ['time', 'volume', 'indicator', 'manual'];

export const ProblemValidationWizard: React.FC<ProblemValidationWizardProps> = ({
  validationConfig,
  monitoringConfig,
  issueStatus,
  onSaveValidation,
  onSaveMonitoring,
  onCompleteValidation,
}) => {
  const [step, setStep] = useState(validationConfig?.started_at ? 2 : 1);
  const [mode, setMode] = useState<ValidationMode>(validationConfig?.mode ?? 'time');
  const [days, setDays] = useState(validationConfig?.time?.validation_days ?? 30);
  const [units, setUnits] = useState(validationConfig?.volume?.validation_units ?? 1000);
  const [target, setTarget] = useState(validationConfig?.indicator?.target_value ?? 98);
  const [saving, setSaving] = useState(false);

  const inputCls = 'w-full rounded border border-slate-300 px-2 py-1.5 text-[13px] dark:border-slate-600 dark:bg-slate-800';

  const handleConfigure = useCallback(async () => {
    setSaving(true);
    const config: ValidationConfig = {
      mode,
      started_at: new Date().toISOString(),
      inspections: validationConfig?.inspections ?? [],
      result: null,
    };
    if (mode === 'time') config.time = { validation_days: days };
    if (mode === 'volume') config.volume = { validation_units: units };
    if (mode === 'indicator') config.indicator = { indicator_id: '', target_value: target, validation_period: 30 };
    await onSaveValidation(config);
    setStep(3);
    setSaving(false);
  }, [mode, days, units, target, validationConfig, onSaveValidation]);

  const handleMonitoring = useCallback(async () => {
    setSaving(true);
    await onSaveMonitoring({ mode: 'manual', started_at: new Date().toISOString(), checks_count: 0 });
    setStep(4);
    setSaving(false);
  }, [onSaveMonitoring]);

  const tracking = (() => {
    if (!validationConfig?.started_at) return null;
    const elapsed = Math.floor((Date.now() - new Date(validationConfig.started_at).getTime()) / 86400000);
    if (validationConfig.mode === 'time' && validationConfig.time) {
      return `${Math.min(elapsed, validationConfig.time.validation_days)} de ${validationConfig.time.validation_days} dias`;
    }
    if (validationConfig.mode === 'volume' && validationConfig.volume) {
      const counted = validationConfig.inspections?.length ?? 0;
      return `${counted} de ${validationConfig.volume.validation_units} unidades`;
    }
    if (validationConfig.mode === 'indicator' && validationConfig.indicator) {
      return `Meta ${validationConfig.indicator.target_value}% · ${validationConfig.indicator.validation_period} dias`;
    }
    return null;
  })();

  const steps = [
    { n: 1, label: 'Definir critério' },
    { n: 2, label: 'Configurar validação' },
    { n: 3, label: 'Aprovar validação' },
    { n: 4, label: 'Monitoramento' },
  ];

  return (
    <div className="mx-auto max-w-2xl space-y-3">
      <div className="flex gap-1">
        {steps.map(s => (
          <div key={s.n} className={`flex-1 rounded px-1 py-1.5 text-center text-[11px] font-semibold ${step >= s.n ? 'bg-purple-600 text-white' : 'bg-slate-100 text-slate-500 dark:bg-slate-800'}`}>
            {s.n}. {s.label}
          </div>
        ))}
      </div>

      {tracking && step >= 2 && (
        <div className="rounded border border-purple-200 bg-purple-50 px-3 py-2 dark:border-purple-800 dark:bg-purple-950/30">
          <p className="text-[10px] text-purple-600">Acompanhamento</p>
          <p className="text-[15px] font-bold text-purple-900 dark:text-purple-200">{tracking}</p>
        </div>
      )}

      {step === 1 && (
        <div className="space-y-2">
          <p className="text-[12px] font-semibold">1. Definir critério</p>
          <p className="text-[11px] text-slate-500">Como deseja validar a eficácia da correção?</p>
          <div className="grid grid-cols-2 gap-2">
            {MODES.map(m => (
              <label key={m} className={`flex cursor-pointer items-center gap-2 rounded border p-2 ${mode === m ? 'border-purple-500 bg-purple-50 dark:bg-purple-950/30' : 'border-slate-200 dark:border-slate-700'}`}>
                <input type="radio" name="vmode" checked={mode === m} onChange={() => setMode(m)} className="accent-purple-600" />
                <span className="text-[11px] font-medium">{VALIDATION_MODE_LABELS[m] === 'Manual' ? 'Aprovação Manual' : VALIDATION_MODE_LABELS[m]}</span>
              </label>
            ))}
          </div>
          <button type="button" onClick={() => setStep(2)} className="flex items-center gap-1 rounded bg-purple-600 px-3 py-1.5 text-[11px] font-semibold text-white">
            Continuar <ChevronRight size={12} />
          </button>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-2">
          <p className="text-[12px] font-semibold">2. Configurar validação</p>
          {mode === 'time' && <label className="block text-[12px] text-slate-600">Dias de acompanhamento<input type="number" min={1} value={days} onChange={e => setDays(+e.target.value)} className={`${inputCls} mt-1`} /></label>}
          {mode === 'volume' && <label className="block text-[12px] text-slate-600">Unidades a acompanhar<input type="number" min={1} value={units} onChange={e => setUnits(+e.target.value)} className={`${inputCls} mt-1`} /></label>}
          {mode === 'indicator' && <label className="block text-[12px] text-slate-600">Meta do indicador (%)<input type="number" min={0} value={target} onChange={e => setTarget(+e.target.value)} className={`${inputCls} mt-1`} /></label>}
          <button type="button" disabled={saving} onClick={handleConfigure} className="rounded bg-purple-600 px-3 py-1.5 text-[11px] font-semibold text-white disabled:opacity-50">
            {saving ? 'Salvando...' : 'Iniciar validação'}
          </button>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-2">
          <p className="text-[12px] font-semibold">3. Registrar o critério</p>
          <p className="text-[11px] text-slate-500">Este registro não confirma a eficácia nem encerra o problema.</p>
          {validationConfig?.result ? (
            <p className="text-[11px] text-slate-600">Critério registrado. A decisão de eficácia fica na verificação humana.</p>
          ) : (
            <div className="flex gap-2">
              <button type="button" onClick={() => onCompleteValidation('passed')} className="flex items-center gap-1 rounded bg-emerald-600 px-3 py-1.5 text-[11px] font-semibold text-white"><Check size={12} /> Critério atendido</button>
              <button type="button" onClick={() => onCompleteValidation('failed')} className="rounded bg-red-600 px-3 py-1.5 text-[11px] font-semibold text-white">Critério não atendido</button>
            </div>
          )}
          {!monitoringConfig?.started_at && (
            <button type="button" disabled={saving} onClick={handleMonitoring} className="text-[11px] font-medium text-blue-600 hover:underline">
              Avançar para monitoramento →
            </button>
          )}
        </div>
      )}

      {step === 4 && (
        <div className="rounded border border-cyan-200 bg-cyan-50 p-3 dark:border-cyan-800 dark:bg-cyan-950/30">
          <p className="text-[12px] font-semibold text-cyan-800 dark:text-cyan-300">4. Monitoramento ativo</p>
          <p className="text-[11px] text-cyan-700 dark:text-cyan-400">
            Verificações: {monitoringConfig?.checks_count ?? 0}
            {monitoringConfig?.last_check_at && ` · Última: ${new Date(monitoringConfig.last_check_at).toLocaleDateString('pt-BR')}`}
          </p>
        </div>
      )}
    </div>
  );
};

export default ProblemValidationWizard;
